<template>
  <div class="custom-fields-manager">
    <div v-if="fields.length === 0" class="text-muted text-center py-3">
      {{ $t('customField.empty') }}
    </div>

    <div v-for="(field, index) in fields" :key="index" class="custom-field-row">
      <template v-if="editingIndex === index">
        <custom-field-form
          :value="editDraft"
          :application-type="applicationType"
          @input="editDraft = $event"
          @save="saveEdit(index)"
          @cancel="editingIndex = null"
        />
      </template>
      <template v-else>
        <div class="custom-field-info">
          <span class="badge badge-secondary mr-2">{{ fieldTypeLabel(field.fieldType) }}</span>
          <span class="font-w600">{{ field.label }}</span>
          <span v-if="field.required" class="badge badge-danger ml-2">{{ $t('required') }}</span>
        </div>
        <div class="custom-field-actions">
          <button type="button" class="btn btn-sm btn-light mr-1" @click="startEdit(index)">
            <i class="fa fa-pencil"></i>
          </button>
          <button type="button" class="btn btn-sm btn-light text-danger" @click="remove(index)">
            <i class="fa fa-trash"></i>
          </button>
        </div>
      </template>
    </div>

    <div v-if="adding" class="mt-3">
      <custom-field-form
        :value="newDraft"
        :application-type="applicationType"
        @input="newDraft = $event"
        @save="saveNew"
        @cancel="adding = false"
      />
    </div>

    <button v-if="!adding && editingIndex === null" type="button" class="btn btn-sm btn-outline-primary mt-3" @click="startAdd">
      <i class="fa fa-plus mr-1"></i>{{ $t('customField.add') }}
    </button>
  </div>
</template>

<script>
import CustomFieldForm from './CustomFieldForm.vue'

const emptyDraft = () => ({
  fieldType: 'input',
  label: '',
  placeholder: '',
  required: false,
  options: []
})

export default {
  components: { CustomFieldForm },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    applicationType: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      adding: false,
      editingIndex: null,
      newDraft: emptyDraft(),
      editDraft: emptyDraft()
    }
  },
  computed: {
    fields () {
      return this.value.filter(f => f.applicationType === this.applicationType)
    }
  },
  methods: {
    fieldTypeLabel (type) {
      const map = {
        input: this.$t('customField.types.input'),
        textarea: this.$t('customField.types.textarea'),
        select: this.$t('customField.types.select'),
        multiselect: this.$t('customField.types.multiselect'),
        boolean: this.$t('customField.types.boolean'),
        confirmation: this.$t('customField.types.confirmation'),
        number: this.$t('customField.types.number'),
        date: this.$t('customField.types.date')
      }
      return map[type] || type
    },
    startAdd () {
      this.newDraft = emptyDraft()
      this.adding = true
    },
    saveNew () {
      const updated = [
        ...this.value,
        { ...this.newDraft, applicationType: this.applicationType }
      ]
      this.$emit('input', updated)
      this.adding = false
    },
    startEdit (index) {
      this.editingIndex = index
      this.editDraft = { ...this.fields[index] }
    },
    saveEdit (localIndex) {
      const globalIndex = this.value.findIndex((f, i) => {
        const filtered = this.value.filter(f2 => f2.applicationType === this.applicationType)
        return f === filtered[localIndex]
      })
      const updated = [...this.value]
      updated[globalIndex] = { ...this.editDraft, applicationType: this.applicationType }
      this.$emit('input', updated)
      this.editingIndex = null
    },
    remove (localIndex) {
      const target = this.fields[localIndex]
      this.$emit('input', this.value.filter(f => f !== target))
    }
  }
}
</script>

<style scoped>
.custom-field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}
.custom-field-row:last-child {
  border-bottom: none;
}
.custom-field-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.custom-field-actions {
  display: flex;
  flex-shrink: 0;
}
</style>
